"use client";

import { useComposedRefs } from "radix-ui/internal";
import { Cross2Icon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import cx from "clsx";
import * as React from "react";
import { useDebouncedCallback } from "use-debounce";
import { IconButton, TextField, TextFieldSlot } from "../elements.js";
import { useApiKeysSearchContext } from "./api-keys-search-provider.js";
import { useApiKeysContext } from "./api-keys-context.js";
import { namespaceClassNames } from "../utils.js";
import { useTranslation } from "../i18n/use-translation.js";

type ApiKeysSearchProps = React.ComponentPropsWithoutRef<typeof TextField>;

export const ApiKeysSearch = React.forwardRef<
  HTMLInputElement,
  ApiKeysSearchProps
>(({ className, ...props }, ref) => {
  const { inputRef, clearSearch, searchValue, setSearchValue } =
    useApiKeysSearchContext();
  const { dispatch } = useApiKeysContext();
  const translate = useTranslation();

  const filter = useDebouncedCallback((value) => {
    dispatch({ type: "FILTER_BY_SEARCH", searchQuery: value });
  }, 200);

  const resetSearch = () => {
    clearSearch();
    filter.cancel();
  };

  return (
    <TextField
      ref={useComposedRefs(inputRef, ref)}
      className={cx(namespaceClassNames("api-keys-search"), className)}
      autoComplete="off"
      placeholder={translate({
        defaultMessage: "Search by name",
        id: "GC8aGI",
        description: "Placeholder for API key search input",
      })}
      value={searchValue}
      onChange={(event) => {
        const value = event.target.value;
        setSearchValue(value);
        filter(value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          resetSearch();
        }
      }}
      {...props}
    >
      <TextFieldSlot side="left">
        <MagnifyingGlassIcon aria-hidden="true" height="16" width="16" />
      </TextFieldSlot>

      <TextFieldSlot side="right">
        {!!searchValue && (
          <IconButton
            size="1"
            onClick={resetSearch}
            title={translate({
              defaultMessage: "Clear search",
              id: "7enwzK",
              description: "Title tooltip for clear search button",
            })}
          >
            <Cross2Icon aria-hidden="true" />
          </IconButton>
        )}
      </TextFieldSlot>
    </TextField>
  );
});
